import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { PropTypes } from 'prop-types';
import { Map, Source, Layer, Popup, Marker } from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';
import { withStyles } from '@mui/styles';
import axios from 'axios';
import {
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
} from '@mui/material';
import TableViewIcon from '@mui/icons-material/TableView';
import ApartmentIcon from '@mui/icons-material/Apartment';
import { GetApp } from '@mui/icons-material';

const mapboxToken =
  'pk.eyJ1IjoiZG9jdGl2aXR5IiwiYSI6ImNsMmo2OWx6bTBtdmUza3J6eDQ2Z3pvdG0ifQ.o9j6xJxOa2WKQLYdqQaJRQ';

const styles = (theme) => ({
  container: {
    width: '100%',
    height: 'calc(100vh - 500px)',
    minHeight: 400,
    position: 'relative'
  },
  header: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
  },
  pdfButton: {
    marginLeft: theme.spacing(),
  },
  pdfIcon: {
    fontSize: 16,
    fontWeight: 200,
  },
  spacer: {
    flex: 1,
  },
  legendContainer: {
    position: 'absolute',
    bottom: 30,
    right: 10,
    backgroundColor: 'rgba(255, 255, 255, 0.9)',
    padding: theme.spacing(1),
    borderRadius: 4,
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    zIndex: 1,
  },
  legendTitle: {
    fontSize: 12,
    fontWeight: 600,
    marginBottom: 4,
  },
  legendScale: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
  },
  legendGradient: {
    width: 120,
    height: 12,
    borderRadius: 2,
  },
  legendLabel: {
    fontSize: 10,
    color: '#666',
  },
  loadingContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 500px)',
    minHeight: 400,
  },
  noDataContainer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 'calc(100vh - 500px)',
    minHeight: 400,
    color: '#666',
    fontSize: 16,
  },
  popup: {
    '& .mapboxgl-popup-content': {
      padding: theme.spacing(1, 2),
      borderRadius: 4,
    },
  },
  popupCounty: {
    fontWeight: 600,
    fontSize: 14,
  },
  popupRow: {
    fontSize: 12,
    color: '#666',
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
  },
  marker: {
    backgroundColor: '#fff',
    borderRadius: '50%',
    padding: 2,
    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))',
    cursor: 'pointer',
  },
  disclaimer: {
    color: '#9E9E9E',
    fontStyle: 'italic',
    fontSize: 13,
    marginRight: 8,
  },
});

// Color scale (soft cream → coral red, 8 stops)
const colorScale = [
  '#FFF9F0', // lightest (soft cream, not stark white)
  '#FFEEB5', // pale yellow
  '#FFDD6A', // yellow
  '#FFCE6A', // yellow-orange
  '#FFBF69', // orange
  '#FFA06E', // peachy orange
  '#FF8073', // lighter coral
  '#FF6178', // darkest (coral red)
];

function OrganizationMigrationHeatMap(props) {
  const {
    classes,
    organization,
    migrations,
    onShowTable,
    onDownloadCSV,
    filters,
    displayMode = 'perCapita', // 'count' or 'perCapita'
  } = props;

  const [countiesGeoJSON, setCountiesGeoJSON] = useState(null);
  const [populationData, setPopulationData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [popupInfo, setPopupInfo] = useState(null);
  const [viewState, setViewState] = useState(null);

  // Load county boundaries GeoJSON
  useEffect(() => {
    axios.get('maps/counties.geojson')
      .then((response) => {
        setCountiesGeoJSON(response.data);
      })
      .catch((error) => {
        console.error('Failed to load county boundaries:', error);
      });
  }, []);

  // Load county population data
  useEffect(() => {
    axios.get('maps/county-population.json')
      .then((response) => {
        setPopulationData(response.data);
        setLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load population data:', error);
        // Continue without population data - per capita won't work
        setPopulationData({});
        setLoading(false);
      });
  }, []);

  // Get organization coordinates for initial center
  const primaryLocation = organization?.primary_location;
  const orgLongitude = primaryLocation?.longitude || -75.65;
  const orgLatitude = primaryLocation?.latitude || 41.40;

  // Process migration data to create county lookup by geo_id
  const { countyData, maxPatients, maxPerCapita, minPatients, minPerCapita, meanPatients, meanPerCapita } = useMemo(() => {
    const data = migrations?.data?.rows || [];
    const lookup = {};
    let maxPat = 0;
    let maxPC = 0;
    let minPat = Infinity;
    let minPC = Infinity;
    const patientValues = [];
    const perCapitaValues = [];

    data.forEach((row) => {
      if (row.county && row.county.geo_id) {
        const geoId = row.county.geo_id;
        const patients = row.total_patients || 0;
        
        if (!lookup[geoId]) {
          lookup[geoId] = {
            geo_id: geoId,
            county_name: row.county.county_name,
            state: row.county.state,
            patients: 0,
            population: populationData?.[geoId] || 0,
          };
        }
        lookup[geoId].patients += patients;
        
        // Update population from loaded data
        if (populationData?.[geoId]) {
          lookup[geoId].population = populationData[geoId];
        }
      }
    });

    // Calculate stats after aggregation
    Object.values(lookup).forEach((county) => {
      const patients = county.patients;
      patientValues.push(patients);
      
      if (patients > maxPat) maxPat = patients;
      if (patients < minPat) minPat = patients;
      
      // Calculate per capita (per 100,000 population)
      if (county.population > 0) {
        const perCapita = (patients / county.population) * 100000;
        county.perCapita = perCapita;
        perCapitaValues.push(perCapita);
        if (perCapita > maxPC) maxPC = perCapita;
        if (perCapita < minPC) minPC = perCapita;
      }
    });

    // Calculate means
    const meanPat = patientValues.length > 0 
      ? patientValues.reduce((sum, v) => sum + v, 0) / patientValues.length 
      : 0;
    const meanPC = perCapitaValues.length > 0 
      ? perCapitaValues.reduce((sum, v) => sum + v, 0) / perCapitaValues.length 
      : 0;

    return {
      countyData: lookup,
      maxPatients: maxPat,
      maxPerCapita: maxPC,
      minPatients: minPat === Infinity ? 0 : minPat,
      minPerCapita: minPC === Infinity ? 0 : minPC,
      meanPatients: meanPat,
      meanPerCapita: meanPC,
    };
  }, [migrations, populationData]);

  // Helper function to normalize value with mean at center
  const normalizeValue = useCallback((value, min, max, mean) => {
    if (min === max) return 0.5;
    if (value <= mean) {
      // Map [min, mean] to [0, 0.5]
      return mean === min ? 0.5 : 0.5 * (value - min) / (mean - min);
    } else {
      // Map [mean, max] to [0.5, 1]
      return mean === max ? 0.5 : 0.5 + 0.5 * (value - mean) / (max - mean);
    }
  }, []);

  // Merge county boundaries with migration data
  const mergedGeoJSON = useMemo(() => {
    if (!countiesGeoJSON || Object.keys(countyData).length === 0) {
      return null;
    }

    const featuresWithData = countiesGeoJSON.features
      .filter((feature) => {
        const geoid = feature.properties?.GEOID;
        return geoid && countyData[geoid];
      })
      .map((feature) => {
        const geoid = feature.properties?.GEOID;
        const data = countyData[geoid];
        
        // Calculate normalized color values (0-1 with mean at 0.5)
        const colorValuePatients = normalizeValue(
          data?.patients || 0, 
          minPatients, 
          maxPatients, 
          meanPatients
        );
        const colorValuePerCapita = normalizeValue(
          data?.perCapita || 0, 
          minPerCapita, 
          maxPerCapita, 
          meanPerCapita
        );
        
        return {
          ...feature,
          properties: {
            ...feature.properties,
            patients: data?.patients || 0,
            population: data?.population || 0,
            perCapita: data?.perCapita || 0,
            colorValuePatients,
            colorValuePerCapita,
            county_name: data?.county_name || feature.properties?.NAME || '',
            state: data?.state || '',
          },
        };
      });

    return {
      type: 'FeatureCollection',
      features: featuresWithData,
    };
  }, [countiesGeoJSON, countyData, minPatients, maxPatients, meanPatients, minPerCapita, maxPerCapita, meanPerCapita, normalizeValue]);

  // Generate color expression based on display mode
  // Uses normalized colorValue (0-1) where mean is at 0.5
  const fillColorExpression = useMemo(() => {
    const colorProperty = displayMode === 'perCapita' ? 'colorValuePerCapita' : 'colorValuePatients';
    
    if (!mergedGeoJSON) {
      return '#FFF9F0';
    }

    // Linear interpolation from 0 to 1 (mean is at 0.5)
    return [
      'interpolate',
      ['linear'],
      ['get', colorProperty],
      0, colorScale[0],
      0.14, colorScale[1],
      0.28, colorScale[2],
      0.42, colorScale[3],
      0.57, colorScale[4],
      0.71, colorScale[5],
      0.85, colorScale[6],
      1, colorScale[7],
    ];
  }, [mergedGeoJSON, displayMode]);

  const handleMapHover = useCallback((event) => {
    const features = event.features;
    if (features && features.length > 0) {
      const feature = features[0];
      const { county_name, state, patients, population, perCapita } = feature.properties;
      
      if (county_name) {
        setPopupInfo({
          longitude: event.lngLat.lng,
          latitude: event.lngLat.lat,
          county_name,
          state,
          patients,
          population,
          perCapita,
        });
      }
    } else {
      setPopupInfo(null);
    }
  }, []);

  const handleMapLeave = useCallback(() => {
    setPopupInfo(null);
  }, []);

  const handleMapMove = useCallback((evt) => {
    setViewState(evt.viewState);
  }, []);

  const initialViewState = viewState || {
    longitude: orgLongitude,
    latitude: orgLatitude,
    zoom: 6,
  };

  if (loading) {
    return (
      <>
        <div className={classes.header}>
          <Typography component='h2' variant='h6' color='primary'>
            Captured Patients by County
          </Typography>
          <div className={classes.pdfButton}>
            <IconButton onClick={onDownloadCSV} size='large'>
              <GetApp className={classes.pdfIcon} />
            </IconButton>
          </div>
          <div className={classes.spacer} />
          <IconButton onClick={onShowTable} size='large'>
            <Tooltip title='Show Table'>
              <TableViewIcon />
            </Tooltip>
          </IconButton>
        </div>
        {filters}
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </>
    );
  }

  const maxValue = displayMode === 'perCapita' ? maxPerCapita : maxPatients;
  const legendLabel = displayMode === 'perCapita' ? 'Per 100K' : 'Patients';
  const hasData = mergedGeoJSON && mergedGeoJSON.features && mergedGeoJSON.features.length > 0;

  // Show no data message if there's no migration data
  if (!hasData) {
    return (
      <>
        <div className={classes.header}>
          <Typography component='h2' variant='h6' color='primary'>
            Captured Patients by County
          </Typography>
          <div className={classes.pdfButton}>
            <IconButton onClick={onDownloadCSV} size='large'>
              <GetApp className={classes.pdfIcon} />
            </IconButton>
          </div>
          <div className={classes.spacer} />
          <IconButton onClick={onShowTable} size='large'>
            <Tooltip title='Show Table'>
              <TableViewIcon />
            </Tooltip>
          </IconButton>
        </div>
        {filters}
        <Box className={classes.noDataContainer}>
          <Typography>No patient migration data available for the selected filters.</Typography>
        </Box>
      </>
    );
  }

  return (
    <>
      <div className={classes.header}>
        <Typography component='h2' variant='h6' color='primary'>
          Captured Patients by County
        </Typography>
        <div className={classes.pdfButton}>
          <IconButton onClick={onDownloadCSV} size='large'>
            <GetApp className={classes.pdfIcon} />
          </IconButton>
        </div>
        <div className={classes.spacer} />
        <IconButton onClick={onShowTable} size='large'>
          <Tooltip title='Show Table'>
            <TableViewIcon />
          </Tooltip>
        </IconButton>
      </div>

      {filters}

      <div className={classes.container}>
        <Map
          {...initialViewState}
          onMove={handleMapMove}
          onMouseMove={handleMapHover}
          onMouseLeave={handleMapLeave}
          mapboxAccessToken={mapboxToken}
          mapStyle="mapbox://styles/mapbox/light-v11"
          interactiveLayerIds={['county-fills']}
          attributionControl={false}
        >
          {mergedGeoJSON && mergedGeoJSON.features.length > 0 && (
            <Source 
              key={`counties-${mergedGeoJSON.features.length}-${displayMode}`}
              id="migration-counties" 
              type="geojson" 
              data={mergedGeoJSON}
            >
              <Layer
                id="county-fills"
                type="fill"
                paint={{
                  'fill-color': fillColorExpression,
                  'fill-opacity': 0.7,
                }}
              />
              <Layer
                id="county-outlines"
                type="line"
                paint={{
                  'line-color': '#f0c0b0',
                  'line-width': 0.5,
                  'line-opacity': 0.3,
                }}
              />
            </Source>
          )}

          {primaryLocation?.latitude && primaryLocation?.longitude && (
            <Marker
              longitude={primaryLocation.longitude}
              latitude={primaryLocation.latitude}
              anchor="center"
            >
              <ApartmentIcon 
                className={classes.marker} 
                style={{ fontSize: 16, color: '#0d47a1' }} 
              />
            </Marker>
          )}

          {popupInfo && (
            <Popup
              longitude={popupInfo.longitude}
              latitude={popupInfo.latitude}
              anchor="bottom"
              closeButton={false}
              closeOnClick={false}
              className={classes.popup}
            >
              <div>
                <div className={classes.popupCounty}>
                  {popupInfo.county_name}, {popupInfo.state}
                </div>
                <div className={classes.popupRow}>
                  <span>Patients:</span>
                  <span>{popupInfo.patients ? popupInfo.patients.toLocaleString() : '< 11'}</span>
                </div>
                {displayMode === 'perCapita' && popupInfo.population > 0 && (
                  <>
                    <div className={classes.popupRow}>
                      <span>Population:</span>
                      <span>{popupInfo.population.toLocaleString()}</span>
                    </div>
                    <div className={classes.popupRow}>
                      <span>Per 100K:</span>
                      <span>{popupInfo.perCapita ? popupInfo.perCapita.toFixed(1) : '—'}</span>
                    </div>
                  </>
                )}
              </div>
            </Popup>
          )}
        </Map>

        {maxValue > 0 && (
          <div className={classes.legendContainer}>
            <div className={classes.legendTitle}>{legendLabel}</div>
            <div className={classes.legendScale}>
              <span className={classes.legendLabel}>0</span>
              <div
                className={classes.legendGradient}
                style={{
                  background: `linear-gradient(to right, ${colorScale.join(', ')})`,
                }}
              />
              <span className={classes.legendLabel}>
                {displayMode === 'perCapita' 
                  ? maxValue.toFixed(0)
                  : maxValue.toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

OrganizationMigrationHeatMap.propTypes = {
  classes: PropTypes.object.isRequired,
  organization: PropTypes.object,
  migrations: PropTypes.object,
  onShowTable: PropTypes.func.isRequired,
  onDownloadCSV: PropTypes.func.isRequired,
  filters: PropTypes.node,
  displayMode: PropTypes.oneOf(['count', 'perCapita']),
};

const styled = withStyles(styles)(OrganizationMigrationHeatMap);
export { styled as OrganizationMigrationHeatMap };